// 참고: https://www.notion.so/hpcnt/API-ffc2a2c9d876475a86fa52d965e5808f 매치 API 가이드문서
import { DTOMatchReportRequest } from 'src/apis/dto/DTOMatchReportRequest';
import { DTOTextChatRequest } from 'src/apis/dto/DTOTextChatRequest';
import { DTOTextChatResponse } from 'src/apis/dto/DTOTextChatResponse';
import { UserEffectSetting } from 'src/types/Deco';
import { InventoryItem } from 'src/types/itemInventory';
import {
  ContentsMatchMessage,
  MatchConfig,
  MatchFilter,
  MatchFilterSelection,
  MatchOptions,
  MLMatchInferences,
  MLMatchMetrics,
  MLMatchSummary,
  PreviousMatchSummary,
  RandomMatchContext,
} from 'src/types/Match';
import { createBeaconApiCall, createApiCall } from 'src/utils/api';
import { PeerConnectionClient } from 'src/utils/webrtc';

export enum MatchApiList {
  /**
   * 크리에이터가 상대방에게 녹화 동의를 요청할 때 호출하는 API
   */
  RequestRecordConsent = '/api/v1/recording-consent/request',

  /**
   * 녹화 동의를 요청받은 유저가 동의 여부에 대한 응답을 보낼 때 호출하는 API
   */
  RespondRecordConsent = '/api/v1/recording-consent/response',
}

export const reportMLMatchInferencesAPI = createBeaconApiCall<MLMatchInferences>(
  '/machine_learning/report/period'
);

export const reportMLMatchSummaryAPI = createBeaconApiCall<MLMatchSummary>(
  '/machine_learning/report/summary'
);

export const reportMLMatchMetricsAPI = createBeaconApiCall<MLMatchMetrics>(
  '/machine_learning/v2/report/metrics/match'
);

interface AbortMatchParams {
  matchId: string;
}

/**
 * 서버에서 매칭된 매치의 중단 API
 */
export const abortMatchAPI = createApiCall<AbortMatchParams, { updatedItems: InventoryItem[] }>(
  'post',
  '/api/v1/match/abort'
);

/**
 * 매칭되기 전 취소 API
 */
export const cancelMatchRandomAPI = createApiCall('post', '/api/v1/match/cancel');

export const listMatchFiltersAPI = createApiCall<void, MatchFilter[]>(
  'get',
  '/api/v1/match/filters'
);

/**
 * 매치풀 등록 API
 */
export const matchRandomAPI = createApiCall<
  {
    matchOptions: MatchOptions;
    matchEffectIds: string[];
    randomMatchContext: RandomMatchContext;
    matchFilterSelections?: MatchFilterSelection[];
    userEffectSetting?: UserEffectSetting;
    previousMatchSummary?: PreviousMatchSummary;
  },
  MatchConfig
>('post', '/api/v1/match/request');

/**
 * 매치 신고 API
 */
export const ReportMatchAPI = createApiCall<DTOMatchReportRequest>('post', '/api/v1/reports/match');

export interface CameraInMatchInfo {
  activatedOnStart: boolean;
  activatedOnEnd: boolean;
  statusChangedTimeMs: number[];
}

interface ReportMatchMetricsParams {
  matchUid: string;
  initiator?: boolean;
  iceConnectTimeMs?: number;
  chatDurationMs?: number;
  finisher?: boolean;
  localCandidateType?: string;
  remoteCandidateType?: string;
  firstFrameReceivedTimeMs?: number;
  facialBlurOnWhenLogin?: boolean;
  facePositions?: number[];
  numRecognizedFace?: number;
  cameraInMatchInfo?: CameraInMatchInfo;
}

/**
 * 매치 지표 분석을 위한 보고 API
 */
export const reportMatchMetricsAPI = createBeaconApiCall<ReportMatchMetricsParams>(
  '/api/v1/match/metrics/reports'
);

/**
 * 일반 매치가 시작됨을 보고하는 API
 */
export const reportMatchStartAPI = createApiCall<
  {
    matchId: string;
    iceConnectTimeMs?: number;
    acceptTimeMs?: number;
    /**
     * MatchInfo 내에 포함된 값을 그대로 다시 전송
     */
    matchContext?: string;
  },
  { updatedItems: InventoryItem[] }
>('post', '/api/v1/match/start');

/**
 * 컨텐츠 매치가 시작됨을 보고하는 API
 */
export const reportContentsMatchStartAPI = createApiCall<{
  contentsMatchId: ContentsMatchMessage['contentsMatchId'];
}>('post', '/api/v1/match/contents-match/start');

/**
 * 컨텐츠 매치가 끝날 때 관련 지표를 보고하는 API
 */
export const reportContentsMatchMetricsAPI = createBeaconApiCall<{
  contentsMatchId: ContentsMatchMessage['contentsMatchId'];
  contentsMatchPlayTimeMs: number;
}>('/api/v1/match/contents-match/metrics');

export const sendTextChatAPI = createApiCall<DTOTextChatRequest, DTOTextChatResponse>(
  'post',
  '/api/v1/match/messages'
);

interface SendWebRTCStatInfoV1Params {
  matchId: string;
  stats: ReturnType<PeerConnectionClient['getWebRTCMatchStats']> | EmptyObject;
}

export const sendWebRTCStatInfoV1API =
  createBeaconApiCall<SendWebRTCStatInfoV1Params>('/api/v1/webrtc/reports');

export interface RequestRecordConsentParams {
  matchId: string;
}

export interface RespondRecordConsentParams {
  matchId: string;
  accepted: boolean;
}

export interface ReportMatchFaceExposureRequest {
  matchId: string;
  cameraEnabled: boolean;
  recognitions: {
    faceCount: number;
    recognizedBy: string;
  }[];
}

export const reportMatchFaceExposureAPI = createApiCall<ReportMatchFaceExposureRequest>(
  'post',
  '/api/v1/match/face-exposure/reports'
);

export interface SpeechRecognitionDataRequest {
  matchId: string;
  sttData: {
    text: string;
    sttCreatedStartAt: number;
    sttCreatedEndAt: number;
    sttConfidence: number | null;
  }[];
  languageCode: string;
  apiCallMillis: number;
}

export const sendSpeechRecognitionDataAPI = createBeaconApiCall<SpeechRecognitionDataRequest>(
  '/api/v1/assistant/speech-recognition-data'
);
